/**
 * Skeleton loaders (2026-05-28).
 *
 * Two exports:
 *   - `<Skeleton>`         — single shimmering bar. Caller controls width/height
 *                            via props; useful inline (e.g. a single value cell).
 *   - `<SkeletonTableRows>` — preformatted N rows × M cells stack for paged
 *                            tables (Alerts, Recordings, Sentries listings).
 *
 * Why this exists: prior loading states were either plain text
 * ("Loading alerts...") or a single grey block, neither of which conveyed
 * the shape of incoming data. The shimmer telegraphs "data is coming" and
 * imitates the row layout the user is about to see, which makes the
 * perceived wait shorter even when total latency is unchanged.
 *
 * Accessibility:
 *   - SkeletonTableRows carries role="status" + aria-label="Loading…" so
 *     screen readers announce the wait without spelling out every cell.
 *   - Individual Skeleton bars are decorative (aria-hidden); the parent
 *     status node is the live region.
 *   - prefers-reduced-motion: reduce halts the shimmer (CSS-level) so
 *     motion-sensitive operators get a static block.
 *
 * Bundle: lives under src/components so the chunk router treats it as
 * entry-friendly. Tiny payload (~50 lines TS + ~40 lines CSS).
 */
import { type CSSProperties, type JSX } from 'react';
import * as styles from './Skeleton.css';

/** Props for the single-bar `<Skeleton>` component. */
export interface SkeletonProps {
  /** Width in CSS units. Numbers are treated as px. Default: '100%'. */
  width?: number | string;
  /** Height in CSS units. Numbers are treated as px. Default: 16. */
  height?: number | string;
  /** Optional extra class concatenated onto the base shimmer class. */
  className?: string;
  /**
   * Extra inline style merged onto the base width/height style. Useful
   * when a parent needs to add `flex: <ratio>` so the skeleton bar
   * participates in a layout (see SkeletonTableRows column-ratio path).
   */
  style?: CSSProperties;
  /** Forwarded onto the root element for tests / probes. */
  'data-testid'?: string;
  /**
   * Render as an inline-block `<span>` instead of a block `<div>`
   * (2026-06-10). Needed when the skeleton replaces text INSIDE a phrasing
   * context (`<span>`, `<p>`, button labels) where a div would be invalid
   * HTML nesting. Default false preserves the original block rendering for
   * every existing caller.
   */
  inline?: boolean;
}

/**
 * Single shimmering bar. Caller provides the dimensions; the component
 * supplies the animation + surface color.
 *
 * @param props see SkeletonProps
 * @returns a decorative `<div>` (or `<span>` when `inline`), aria-hidden
 */
export function Skeleton(props: SkeletonProps): JSX.Element {
  const { width = '100%', height = 16, className, style, inline = false, ...rest } = props;
  const mergedStyle: CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    // Inline mode: span defaults to display:inline, which collapses the
    // height; inline-block keeps the bar dimensions while staying legal
    // inside phrasing content. vertical-align centers it against text.
    ...(inline ? { display: 'inline-block', verticalAlign: 'middle' } : {}),
    ...(style ?? {}),
  };
  const cls = className ? `${styles.skeleton} ${className}` : styles.skeleton;
  if (inline) {
    return (
      <span
        className={cls}
        style={mergedStyle}
        data-testid={rest['data-testid']}
        aria-hidden="true"
      />
    );
  }
  return (
    <div
      className={cls}
      style={mergedStyle}
      data-testid={rest['data-testid']}
      aria-hidden="true"
    />
  );
}

/** Props for `<SkeletonTableRows>`. */
export interface SkeletonTableRowsProps {
  /** Number of skeleton rows to render. */
  rows: number;
  /** Cells per row (equal width unless `widths` is provided). */
  cells: number;
  /**
   * Optional per-column flex ratios so the skeleton mirrors the real
   * table layout. Length should equal `cells`; if shorter, missing
   * entries default to 1 (equal-width). If omitted entirely, every
   * cell uses flex:1 (the original behaviour).
   *
   * Example: a 7-column alerts table with wider Name + Evidence columns
   * could pass `[1, 1, 2, 1, 1, 2, 1]`. The layout-shift between the
   * skeleton and the real table is then minimal, which fixes the
   * "skeleton looks generic" UX feedback on v0.14.4.
   */
  widths?: readonly number[];
  /** Forwarded onto the wrapper so existing testids stay stable. */
  'data-testid'?: string;
  /** Optional aria-label override; defaults to "Loading". */
  'aria-label'?: string;
}

/**
 * Stack of skeleton rows shaped like a table. Use as a direct replacement
 * for a "Loading…" placeholder above a table component.
 *
 * The wrapper is the live region (role="status"), individual bars are
 * decorative. Renders rows × cells DIVs total, each absolutely cheap.
 */
export function SkeletonTableRows(
  props: SkeletonTableRowsProps,
): JSX.Element {
  const { rows, cells, widths, ...rest } = props;
  const label = rest['aria-label'] ?? 'Loading';
  return (
    <div
      className={styles.tableRows}
      role="status"
      aria-label={label}
      aria-live="polite"
      data-testid={rest['data-testid']}
    >
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className={styles.row} aria-hidden="true">
          {Array.from({ length: cells }, (_, c) => {
            // Resolve the flex ratio for this cell: explicit `widths[c]`
            // wins when provided, otherwise fall through to the equal-flex
            // baseline that the `cell` style class already supplies.
            const ratio = widths?.[c];
            const cellStyle: CSSProperties | undefined =
              typeof ratio === 'number'
                ? { flex: `${ratio} ${ratio} 0` }
                : undefined;
            return (
              <Skeleton
                key={c}
                className={styles.cell}
                style={cellStyle}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
